import React, { useState } from 'react';
import { 
  Palette, 
  Settings, 
  Monitor, 
  Smartphone, 
  Eye, 
  Zap, 
  RotateCcw,
  Check,
  X,
  Upload,
  Type,
  Sliders
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const ThemeCustomizer = ({ isOpen, onClose }) => {
  const { settings, currentTheme, themes, updateTheme, updateSettings, resetToDefaults } = useTheme();
  const [activeTab, setActiveTab] = useState('themes');

  if (!isOpen) return null;

  const tabs = [
    { id: 'themes', label: 'Temas', icon: Palette },
    { id: 'appearance', label: 'Apariencia', icon: Eye },
    { id: 'behavior', label: 'Comportamiento', icon: Settings }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-2 md:p-4">
      <div className="w-full max-w-4xl h-full md:h-auto bg-white rounded-xl md:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-purple-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-emerald-500 to-purple-600 rounded-lg md:rounded-xl flex items-center justify-center">
              <Palette className="w-4 h-4 md:w-5 md:h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-bold text-gray-900">
                Personalizar Tema
              </h2>
              <p className="text-xs md:text-sm text-gray-600 hidden sm:block">
                Customiza la apariencia y comportamiento del sidebar
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={resetToDefaults}
              className="px-2 py-1 md:px-3 md:py-2 text-xs md:text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md md:rounded-lg transition-colors flex items-center gap-1 md:gap-2"
            >
              <RotateCcw className="w-3 h-3 md:w-4 md:h-4" />
              <span className="hidden sm:inline">Restaurar</span>
            </button>
            <button
              onClick={onClose}
              className="p-1 md:p-2 hover:bg-gray-100 rounded-md md:rounded-lg transition-colors"
            >
              <X className="w-4 h-4 md:w-5 md:h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Pestañas móviles (horizontal) */}
        <div className="md:hidden border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <nav className="flex overflow-x-auto">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === id
                    ? 'bg-emerald-100 text-emerald-700 border-b-2 border-emerald-500'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Sidebar de pestañas (desktop) */}
          <div className="hidden md:block w-64 bg-gray-50 border-r border-gray-200">
            <nav className="p-4 space-y-2">
              {tabs.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                    activeTab === id
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </nav>
          </div>

          {/* Contenido */}
          <div className="flex-1 p-3 md:p-6 overflow-y-auto">
            {activeTab === 'themes' && (
              <div className="space-y-4 md:space-y-6">
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">
                    Temas Predefinidos
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(themes).map(([key, theme]) => (
                      <div
                        key={key}
                        className={`relative p-3 border-2 rounded-xl cursor-pointer transition-all hover:shadow-md ${
                          settings.theme === key
                            ? 'border-emerald-500 bg-emerald-50 shadow-sm'
                            : 'border-gray-200 hover:border-emerald-200'
                        }`}
                        onClick={() => updateTheme(key)}
                      >
                        {settings.theme === key && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center z-10">
                            <Check className="w-2.5 h-2.5 text-white" />
                          </div>
                        )}

                        {/* Preview gradient */}
                        <div className={`h-11 bg-gradient-to-b ${theme.sidebar.background} rounded-lg mb-2 relative overflow-hidden`}>
                          <div className={`absolute inset-0 bg-gradient-to-r ${theme.sidebar.headerBg} opacity-60`}></div>
                          <div className="absolute inset-0 flex items-center justify-around px-2">
                            <div className={`w-1 h-5 rounded-full bg-${theme.accent.primary} opacity-80`}></div>
                            <div className={`w-1 h-7 rounded-full bg-${theme.accent.hover} opacity-90`}></div>
                            <div className={`w-1 h-4 rounded-full bg-${theme.accent.secondary} opacity-60`}></div>
                            <div className={`w-1 h-6 rounded-full bg-${theme.accent.primary} opacity-70`}></div>
                          </div>
                        </div>

                        {/* Name */}
                        <div className="flex items-center gap-1.5 pr-5">
                          <div className={`w-4 h-4 bg-gradient-to-br ${theme.logo.bg} rounded flex-shrink-0`}></div>
                          <span className="text-xs font-medium text-gray-800 truncate">{theme.name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'appearance' && (
              <div className="space-y-4 md:space-y-6">
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">
                    Personalización Visual
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Logo personalizado */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Logo de la Empresa
                      </label>
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 md:gap-3">
                          <div className={`w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br ${currentTheme.logo.bg} rounded-lg md:rounded-xl flex items-center justify-center shadow-lg overflow-hidden`}>
                            {settings.customLogo ? (
                              <img src={settings.customLogo} alt="Logo" className="w-full h-full object-cover" />
                            ) : (
                              <Zap className="w-5 h-5 md:w-6 md:h-6 text-white" />
                            )}
                          </div>
                          <div className="flex-1">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onload = (event) => {
                                    updateSettings({ customLogo: event.target.result });
                                  };
                                  reader.readAsDataURL(file);
                                }
                              }}
                              className="hidden"
                              id="logo-upload"
                            />
                            <label
                              htmlFor="logo-upload"
                              className="inline-flex items-center gap-1 md:gap-2 px-2 py-1.5 md:px-3 md:py-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-md md:rounded-lg cursor-pointer transition-colors text-sm"
                            >
                              <Upload className="w-3 h-3 md:w-4 md:h-4" />
                              <span className="hidden sm:inline">Subir Logo</span>
                              <span className="sm:hidden">Logo</span>
                            </label>
                          </div>
                          {settings.customLogo && (
                            <button
                              onClick={() => updateSettings({ customLogo: null })}
                              className="px-2 py-1.5 md:px-3 md:py-2 text-xs md:text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md md:rounded-lg transition-colors"
                            >
                              Quitar
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          Sube una imagen PNG, JPG o SVG. Recomendado: formato cuadrado.
                        </p>
                      </div>
                    </div>

                    {/* Nombre de la aplicación */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre de la Aplicación
                      </label>
                      <div className="flex items-center gap-2">
                        <Type className="w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={settings.appName}
                          onChange={(e) => updateSettings({ appName: e.target.value })}
                          className="flex-1 px-2 py-1.5 md:px-3 md:py-2 text-sm border border-gray-300 rounded-md md:rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          placeholder="Elements"
                        />
                      </div>
                    </div>

                    {/* Efectos visuales */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Efectos Visuales
                      </label>
                      
                      <div className="space-y-2">
                        <label className="flex items-start md:items-center">
                          <input
                            type="checkbox"
                            checked={settings.animationsEnabled}
                            onChange={(e) => updateSettings({ animationsEnabled: e.target.checked })}
                            className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 mt-0.5 md:mt-0"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            Habilitar animaciones
                          </span>
                        </label>
                        
                        <label className="flex items-start md:items-center">
                          <input
                            type="checkbox"
                            checked={settings.glassEffect}
                            onChange={(e) => updateSettings({ glassEffect: e.target.checked })}
                            className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 mt-0.5 md:mt-0"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            Efecto cristal (glassmorphism)
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Posición del sidebar */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Posición del Sidebar
                      </label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => updateSettings({ sidebarPosition: 'left' })}
                          className={`flex-1 px-2 py-2 md:px-3 md:py-2 rounded-md md:rounded-lg border flex items-center justify-center gap-1 md:gap-2 text-sm ${
                            settings.sidebarPosition === 'left'
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <Monitor className="w-3 h-3 md:w-4 md:h-4" />
                          <span className="hidden sm:inline">Izquierda</span>
                          <span className="sm:hidden">Izq</span>
                        </button>
                        <button
                          onClick={() => updateSettings({ sidebarPosition: 'right' })}
                          className={`flex-1 px-2 py-2 md:px-3 md:py-2 rounded-md md:rounded-lg border flex items-center justify-center gap-1 md:gap-2 text-sm ${
                            settings.sidebarPosition === 'right'
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <Monitor className="w-3 h-3 md:w-4 md:h-4 transform scale-x-[-1]" />
                          <span className="hidden sm:inline">Derecha</span>
                          <span className="sm:hidden">Der</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'behavior' && (
              <div className="space-y-4 md:space-y-6">
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">
                    Comportamiento del Sidebar
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <label className="flex items-start md:items-center">
                        <input
                          type="checkbox"
                          checked={settings.autoCollapse}
                          onChange={(e) => updateSettings({ autoCollapse: e.target.checked })}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 mt-0.5 md:mt-0"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Auto-colapsar en pantallas pequeñas
                        </span>
                      </label>
                      
                      <label className="flex items-start md:items-center">
                        <input
                          type="checkbox"
                          checked={settings.showTooltips}
                          onChange={(e) => updateSettings({ showTooltips: e.target.checked })}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 mt-0.5 md:mt-0"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Mostrar tooltips en modo colapsado
                        </span>
                      </label>
                      
                      <label className="flex items-start md:items-center">
                        <input
                          type="checkbox"
                          checked={settings.compactMode}
                          onChange={(e) => updateSettings({ compactMode: e.target.checked })}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 mt-0.5 md:mt-0"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Modo compacto (espaciado reducido)
                        </span>
                      </label>
                      
                      <label className="flex items-start md:items-center">
                        <input
                          type="checkbox"
                          checked={settings.showBadges}
                          onChange={(e) => updateSettings({ showBadges: e.target.checked })}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 mt-0.5 md:mt-0"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Mostrar badges y notificaciones
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0 p-3 md:p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="text-xs md:text-sm text-gray-600 text-center sm:text-left">
            Los cambios se guardan automáticamente
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 md:px-6 md:py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md md:rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
          >
            <Check className="w-3 h-3 md:w-4 md:h-4" />
            Listo
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThemeCustomizer;